import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Observable, of, Subject } from 'rxjs';
import { Person } from 'src/app/people/models/person';

import { EmailType, IEmailMessageWrite } from 'src/app/shared/models/emailMessage';
import { BulkEmailMessageWrite } from '../models/bulkEmailMessageWrite';
import { EmailCustomization } from '../models/emailCustomization';
import { ApiVersions } from './api-version';
import { BaseService } from './base.service';
import { EmailPreferencesService } from './email-preferences.service';

const MaxEmailRecipientCount = 10_000;

@Injectable()
export class EmailPreparationService extends BaseService {
  public readonly emailSent = new Subject();

  private baseUsersPath: string = 'users';
  private onPreparedCallback: (result: EmailPreparedResult) => void;

  constructor(private emailPreferencesService: EmailPreferencesService) {
    super();
  }

  openSendEmailDrawer(
    relativeTo: ActivatedRoute,
    preparation: EmailPreparation,
    onPrepared: (result: EmailPreparedResult) => void
  ) {
    this.onPreparedCallback = onPrepared;
    this.router.navigate(['sendEmail'], {
      state: {
        data: preparation,
      },
      relativeTo: relativeTo,
    });
  }

  onEmailPrepared(result: EmailPreparedResult) {
    this.onPreparedCallback(result);
    this.onPreparedCallback = null;
    this.emailSent.next();
  }

  sendIndividualEmail(userId: string, message: IEmailMessageWrite) {
    let payload = JSON.stringify(message);
    return this.post(`${this.baseUsersPath}/${userId}/email`, payload).subscribe(() => {
      this.openSnackBar(`Successfully sent email.`, true);
    });
  }

  sendBulkEmail(message: BulkEmailMessageWrite) {
    return this.post(
      `${this.baseUsersPath}/bulk-email`,
      JSON.stringify(message),
      null,
      ApiVersions.v0_1,
      true // rethrow errors to prevent the success callback if there's an error
    ).subscribe(() => {
      this.openSnackBar('Successfully sent email.', true);
    });
  }

  prepareFooter(data: EmailPreparation, isRecipientInvalid: boolean): string {
    var footer: string = '';

    if (data?.person) {
      var person: Person = data?.person;
      footer = isRecipientInvalid
        ? person.fullName +
          ' (' +
          person.email +
          ') has opted out of receiving group and individual emails.'
        : 'Send email to ' + person.fullName + ' (' + person.email + ') ';
      return footer;
    }
    if (data?.filteredPeopleCount) {
      footer = isRecipientInvalid
        ? `Cannot send to more than ${MaxEmailRecipientCount} recipients at a time.`
        : `Send email to ${data?.filteredPeopleCount} people.`;
      return footer;
    }

    return footer;
  }

  isRecipientInvalid(data: EmailPreparation): Observable<boolean> {
    var isRecipientInvalid;
    if (data?.person) {
      var person: Person = data?.person;
      var subject = new Subject<boolean>();
      this.emailPreferencesService
        .getCommercialEmailPreference(person.id)
        .subscribe((emailPref) => {
          isRecipientInvalid = !emailPref.isOptedIn;
          subject.next(isRecipientInvalid);
        });

      return subject.asObservable();
    }

    if (data?.filteredPeopleCount) {
      isRecipientInvalid = data?.filteredPeopleCount > MaxEmailRecipientCount;
      return of(isRecipientInvalid);
    }

    return of(true);
  }
}

export interface EmailPreparation {
  emailType: EmailType;
  title: string;
  templateName: string;
  person?: Person;
  peopleIds?: string[];
  filteredPeopleCount?: number;
  emailCustomization?: EmailCustomization;
}

export interface EmailPreparedResult {
  emailType: EmailType;
  emailMessage: IEmailMessageWrite;
}
